import * as PortOne from '@portone/browser-sdk/v2';
import { auth } from '../firebase';

export interface PortOnePaymentParams {
  tier: string;
  orderName: string;
  amount: number;
  currency?: 'KRW' | 'USD';
  billingCycle?: 'monthly' | 'annual';
  isAddon?: boolean;
  customerName?: string;
  customerEmail?: string;
  customerPhoneNumber?: string;
}

// 포트원 V2 스토어/채널 식별자 (VITE_ 접두사는 클라이언트에 노출되는 공개 값)
// 실제 값은 PortOne 관리자 콘솔(admin.portone.io) > 결제 연동 > 채널 에서 확인합니다.
const PORTONE_STORE_ID = import.meta.env.VITE_PORTONE_STORE_ID || 'store-bd7cb015-e483-4904-b371-74dc153c6154';
const PORTONE_CHANNEL_KEY = import.meta.env.VITE_PORTONE_CHANNEL_KEY || 'channel-key-9109cbdf-93b3-4b5f-822f-10ddd13d3260';

/**
 * 포트원 결제/빌링키 발급 요청에 실어 보낼 귀속 정보(customData).
 *
 * 브라우저 콜백이 유실되어 웹훅(Transaction.Paid / BillingKey.Issued)만 도착하는 경우에도
 * 서버(functions/api/payment/portone/webhook.ts)가 사용자·상품을 식별할 수 있게 합니다.
 * customData 는 클라이언트가 지정할 수 있으므로, 서버는 반드시 포트원 결제 내역의
 * 실결제 금액(amount.total)을 서버 기준 가격과 재검증한 뒤에만 권한을 부여합니다.
 */
function buildPortOneCustomData(params: PortOnePaymentParams): Record<string, any> {
  return {
    userId: auth.currentUser?.uid || '',
    tier: params.tier,
    billingCycle: params.billingCycle || 'monthly',
    isAddon: params.isAddon ?? false
  };
}

/**
 * 포트원 V2 단건 결제 요청 및 백엔드 승인
 */
export async function requestPortOnePayment(params: PortOnePaymentParams): Promise<{
  success: boolean;
  subscriptionId?: string;
  expiresAt?: string;
  error?: string;
}> {
  const paymentId = `p1_pay_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  try {
    const user = auth.currentUser;
    const token = await user?.getIdToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    const response = await PortOne.requestPayment({
      storeId: PORTONE_STORE_ID,
      channelKey: PORTONE_CHANNEL_KEY,
      paymentId,
      orderName: params.orderName,
      totalAmount: params.amount,
      currency: (params.currency || 'KRW') as any,
      payMethod: 'CARD',
      // 웹훅(Transaction.Paid)만 도달한 경우에도 서버가 사용자/상품을 식별할 수 있도록 결제에 귀속 정보를 실어 보냅니다.
      // (실제 권한 부여는 서버에서 금액 재검증 후에만 이루어지므로 위변조에 안전합니다.)
      customData: buildPortOneCustomData(params),
      customer: {
        fullName: params.customerName || user.displayName || 'EZOptix 회원',
        email: params.customerEmail || user.email || 'customer@ezoptix.ai',
        phoneNumber: params.customerPhoneNumber || '010-0000-0000',
      }
    });

    if (!response || response.code !== undefined) {
      // 결제 취소 또는 실패
      return { success: false, error: response?.message || '결제가 취소되었습니다.' };
    }

    if (!response.paymentId || response.paymentId !== paymentId) {
      return { success: false, error: '결제 인증 응답이 유효하지 않습니다. 다시 시도해 주세요.' };
    }

    // 백엔드 사후 검증
    const confirmRes = await fetch('/api/payment/portone/confirm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        paymentId,
        tier: params.tier,
        billingCycle: params.billingCycle || 'monthly',
        expectedAmount: params.amount,
        isAddon: params.isAddon ?? false
      })
    });

    const confirmData = await confirmRes.json() as any;
    if (!confirmRes.ok) {
      throw new Error(confirmData.error || '결제 승인 검증에 실패했습니다.');
    }

    return {
      success: true,
      subscriptionId: confirmData.subscriptionId,
      expiresAt: confirmData.expiresAt
    };
  } catch (err: any) {
    console.error('[PortOne Payment Error]:', err);
    return { success: false, error: err.message || '결제 처리 중 오류가 발생했습니다.' };
  }
}

/**
 * 포트원 V2 정기결제 빌링키 발급 및 첫 회 결제
 */
export async function requestPortOneSubscription(params: PortOnePaymentParams): Promise<{
  success: boolean;
  subscriptionId?: string;
  expiresAt?: string;
  error?: string;
}> {
  const issueId = `p1_issue_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('로그인이 필요합니다.');
    }

    // 나이스페이 등 일부 PG의 빌링키 발급 창은 POPUP 방식으로 열립니다.
    // 브라우저 팝업 차단을 피하려면 사용자 클릭 제스처가 유효한 동안(= 추가 await 없이) 창을 호출해야 하므로,
    // 토큰 발급(await)은 창을 띄운 뒤로 미룹니다. 토큰은 이후 백엔드 호출에만 필요합니다.
    const response = await PortOne.requestIssueBillingKey({
      storeId: PORTONE_STORE_ID,
      channelKey: PORTONE_CHANNEL_KEY,
      billingKeyMethod: 'CARD',
      // 나이스페이(NICEPAY) 등 일부 PG는 빌링키 발급 시 '주문 번호(주문 고유 번호)'를 필수로 요구합니다.
      // 누락하면 checkout-service 가 400 을 반환하므로("...주문 번호는 필수 입력입니다.") 반드시 전달합니다.
      issueId,
      issueName: `${params.orderName} 정기결제 등록`,
      // 웹훅(BillingKey.Issued)만 도달한 경우에도 서버가 사용자를 식별할 수 있도록 귀속 정보를 저장합니다.
      customData: buildPortOneCustomData(params),
      customer: {
        customerId: user.uid,
        fullName: params.customerName || user.displayName || 'EZOptix 회원',
        email: params.customerEmail || user.email || 'customer@ezoptix.ai',
        phoneNumber: params.customerPhoneNumber || '010-0000-0000',
      }
    });

    if (response?.code !== undefined) {
      return { success: false, error: response.message || '빌링키 등록이 취소되었습니다.' };
    }

    const billingKey = response?.billingKey;
    if (!billingKey) {
      throw new Error('빌링키를 발급받지 못했습니다.');
    }

    // 창이 정상적으로 열린 뒤에 토큰을 발급받습니다(위에서 await 를 하지 않아 팝업 차단을 방지).
    const token = await user.getIdToken();
    if (!token) {
      throw new Error('로그인이 필요합니다.');
    }

    // 백엔드 빌링키 저장 및 첫 회 결제 집행
    const issueRes = await fetch('/api/payment/portone/billing-issue', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        billingKey,
        tier: params.tier,
        billingCycle: params.billingCycle || 'monthly',
        amount: params.amount,
        isAddon: params.isAddon ?? false
      })
    });

    const issueData = await issueRes.json() as any;
    if (!issueRes.ok) {
      throw new Error(issueData.error || '정기결제 등록 처리에 실패했습니다.');
    }

    return {
      success: true,
      subscriptionId: issueData.subscriptionId,
      expiresAt: issueData.expiresAt
    };
  } catch (err: any) {
    console.error('[PortOne Subscription Error]:', err);
    return { success: false, error: err.message || '정기결제 신청 중 오류가 발생했습니다.' };
  }
}
